import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Trash2, ShoppingCart } from 'lucide-react';
import { STORAGE_BASE } from '../config';
import CustomerAccount from '../components/CustomerAccount';

const MD = {
  primary: 'var(--primary)',
  onVariant: 'var(--muted-fg)',
  success: '#16A34A',
  error: '#DC2626',
  card: { background: 'var(--card)', borderRadius: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.14)' },
  btn: { padding: '9px 18px', borderRadius: 20, border: 'none', background: 'var(--primary)', color: 'var(--on-primary)', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.85rem' },
  btnOutline: { padding: '8px 14px', borderRadius: 20, border: '1px solid var(--border)', background: 'var(--card)', color: 'var(--primary)', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.82rem', display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none' },
};
const fa = n => Number(n || 0).toLocaleString('fa-IR');
const authH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('customer_token') || ''}` } });

export default function ComparePage({ embedded, onDone }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = (() => { try { return localStorage.getItem('customer_token'); } catch { return null; } })();

  const load = () => {
    if (!localStorage.getItem('customer_token')) { setLoading(false); return; }
    setLoading(true);
    axios.get('/compare', authH())
      .then(r => setRows(Array.isArray(r.data) ? r.data : []))
      .catch(e => toast.error(e.response?.data?.message || 'خطا در دریافت مقایسه.', { position: 'bottom-right', autoClose: 5000, theme: 'colored' }))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const remove = async id => {
    try {
      await axios.delete(`/compare/${id}`, authH());
      setRows(rows.filter(x => x.id !== id));
      window.dispatchEvent(new Event('compare-updated'));
    } catch (e) { toast.error('خطا در حذف.', { position: 'bottom-right', autoClose: 5000, theme: 'colored' }); }
  };

  const addCart = async pid => {
    try {
      await axios.post('/cart/add', { product_id: pid, qty: 1 }, authH());
      window.dispatchEvent(new Event('cart-updated'));
      toast.success('به سبد خرید اضافه شد.', { position: 'bottom-right', autoClose: 5000, theme: 'colored' });
    } catch (e) { toast.error(e.response?.data?.message || 'خطا.', { position: 'bottom-right', autoClose: 5000, theme: 'colored' }); }
  };

  if (!token) {
    return (
      <div style={embedded ? { direction: 'rtl' } : { padding: '40px 16px', direction: 'rtl', maxWidth: 560 }}>
        {!embedded && <ToastContainer position="bottom-right" autoClose={5000} rtl theme="colored" />}
        {embedded ? (
          <>
            <p style={{ color: MD.onVariant, fontSize: '0.9rem', margin: '0 0 16px', textAlign: 'center' }}>برای مشاهده مقایسه ابتدا وارد شوید.</p>
            <CustomerAccount compact onDone={() => window.location.reload()} />
          </>
        ) : (
          <div style={{ ...MD.card, padding: 28, textAlign: 'center' }}>
            <h2 style={{ margin: '0 0 8px', fontSize: '1.2rem', fontWeight: 800 }}>مقایسه محصولات</h2>
            <p style={{ color: MD.onVariant, fontSize: '0.9rem', margin: '0 0 16px' }}>برای مشاهده مقایسه ابتدا وارد شوید.</p>
            <CustomerAccount compact onDone={() => window.location.reload()} />
          </div>
        )}
      </div>
    );
  }

  const products = rows.map(r => r.product).filter(Boolean);
  const allAttrs = [...new Set(products.flatMap(p => Object.keys(p.attributes || {})))];
  const allFields = [...new Set(products.flatMap(p => (p.customization_data?.fields || []).map(f => f.name)))];

  const specRow = (label, render) => (
    <tr style={{ borderTop: '1px solid #F4F4F5' }}>
      <td style={{ padding: '12px 14px', fontSize: '0.82rem', fontWeight: 700, color: MD.onVariant, whiteSpace: 'nowrap', background: 'var(--card)' }}>{label}</td>
      {rows.map(r => <td key={r.id} style={{ padding: '12px 14px', fontSize: '0.88rem', textAlign: 'center' }}>{render(r.product)}</td>)}
    </tr>
  );

  if (embedded && !loading && rows.length > 0) {
    const goCompare = () => { if (onDone) onDone(); navigate('/compare'); };
    return (
      <div style={{ direction: 'rtl' }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {rows.map(r => {
            const p = r.product || {};
            const src = p.image_path ? `${STORAGE_BASE}/${String(p.image_path).replace(/^\/+/, '')}` : 'https://via.placeholder.com/200x200?text=Silver';
            return (
              <div key={r.id} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                <Link to={`/products/${p.id}`} onClick={() => onDone && onDone()}><img src={src} alt={p.name} style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 12, background: 'var(--muted)', display: 'block' }} /></Link>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Link to={`/products/${p.id}`} onClick={() => onDone && onDone()} style={{ fontWeight: 700, color: 'var(--fg)', textDecoration: 'none', fontSize: '0.88rem', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</Link>
                  <div style={{ fontSize: '0.8rem', color: 'var(--muted-fg)', marginTop: 2 }}>{fa(Number(p.price) || 0)} تومان</div>
                </div>
                <button onClick={() => remove(r.id)} aria-label="حذف" style={{ width: 34, height: 34, borderRadius: 10, border: '1px solid var(--border)', background: 'var(--card)', color: 'var(--muted-fg)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '1rem' }}>×</button>
              </div>
            );
          })}
        </div>
        <button onClick={goCompare} style={{ width: '100%', padding: 13, borderRadius: 12, border: '1.5px solid var(--border)', background: 'var(--card)', color: 'var(--fg)', fontFamily: 'inherit', fontSize: '0.92rem', fontWeight: 800, cursor: 'pointer', marginTop: 14 }}>مشاهده مقایسه کامل</button>
      </div>
    );
  }

  return (
    <div style={embedded ? { direction: 'rtl' } : { padding: '28px 16px 60px', direction: 'rtl', maxWidth: 1100 }}>
      {!embedded && <ToastContainer position="bottom-right" autoClose={5000} rtl theme="colored" />}
      {!embedded && <h1 style={{ fontSize: '1.4rem', fontWeight: 800, margin: '0 0 18px' }}>مقایسه محصولات</h1>}
      {loading ? (
        <div style={{ ...MD.card, padding: 40, textAlign: 'center', color: MD.onVariant }}>در حال بارگذاری...</div>
      ) : !rows.length ? (
        <div style={{ ...MD.card, padding: 40, textAlign: 'center' }}>
          <p style={{ color: MD.onVariant, margin: '0 0 16px' }}>هنوز محصولی برای مقایسه انتخاب نشده است.</p>
          <Link to="/products" style={{ ...MD.btn, textDecoration: 'none', display: 'inline-block' }}>مشاهده محصولات</Link>
        </div>
      ) : (
        <div style={{ ...MD.card, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
            <thead>
              <tr>
                <td style={{ padding: 14, background: 'var(--card)' }} />
                {rows.map(r => {
                  const p = r.product || {};
                  const src = p.image_path ? `${STORAGE_BASE}/${String(p.image_path).replace(/^\/\/+/, '')}` : 'https://via.placeholder.com/200x200?text=Silver';
                  return (
                    <td key={r.id} style={{ padding: 14, textAlign: 'center', minWidth: 190 }}>
                      <button onClick={() => remove(r.id)} style={{ ...MD.btnOutline, borderColor: 'var(--border)', color: MD.error, marginBottom: 8 }}><Trash2 size={14} /> حذف</button>
                      <Link to={`/products/${p.id}`}><img src={src} alt={p.name} style={{ width: '100%', maxWidth: 190, aspectRatio: 1, objectFit: 'cover', borderRadius: 12, background: 'var(--muted)' }} /></Link>
                      <Link to={`/products/${p.id}`} style={{ display: 'block', fontWeight: 800, color: 'var(--card-fg)', textDecoration: 'none', marginTop: 8, fontSize: '0.92rem' }}>{p.name}</Link>
                      <button onClick={() => addCart(p.id)} style={{ ...MD.btn, marginTop: 8, width: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6 }}><ShoppingCart size={14} /> افزودن به سبد</button>
                    </td>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {specRow('قیمت پایه', p => <b>{fa(p.customization_data?.base_price || p.price || 0)} تومان</b>)}
              {specRow('موجودی', p => <b style={{ color: (p.stock || 0) < 5 ? MD.error : MD.success }}>{fa(p.stock || 0)}</b>)}
              {specRow('محدودیت خرید', p => `${fa(p.min_purchase || 1)} تا ${fa(p.max_purchase || 100)}`)}
              {allAttrs.map(a => specRow(a, p => (p.attributes || {})[a] || '—'))}
              {allFields.map(f => specRow(`ویژگی: ${f}`, p => ((p.customization_data?.fields || []).some(x => x.name === f) ? 'دارد' : '—')))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
